import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { SPA_ROUTES } from '@/lib/paths';
import { RequireAuth } from './RequireAuth';
import LoginPage from '@/pages/LoginPage/LoginPage';
const AppLaunchingPage = lazy(() => import('@/pages/AppLaunchingPage/AppLaunchingPage'));
const FederatedLoginPage = lazy(() => import('@/pages/FederatedLoginPage/FederatedLoginPage'));
const LogoutPage = lazy(() => import('@/pages/LogoutPage/LogoutPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage/ForgotPasswordPage'));
const ForgotPasswordConfirmationPage = lazy(
  () => import('@/pages/ForgotPasswordPage/ForgotPasswordConfirmationPage'),
);
const LockoutPage = lazy(() => import('@/pages/LockoutPage/LockoutPage'));
const MfaChallengePage = lazy(() => import('@/pages/MfaChallengePage/MfaChallengePage'));
const AccessDeniedPage = lazy(() => import('@/pages/AccessDeniedPage/AccessDeniedPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage/ResetPasswordPage'));
const ResetPasswordConfirmationPage = lazy(
  () => import('@/pages/ResetPasswordPage/ResetPasswordConfirmationPage'),
);
const PasswordResetRequiredPage = lazy(
  () => import('@/pages/ResetPasswordPage/PasswordResetRequiredPage'),
);
const ConfirmAccountPage = lazy(() => import('@/pages/ConfirmAccountPage/ConfirmAccountPage'));
const ConfirmAccountRequiredPage = lazy(
  () => import('@/pages/ConfirmAccountPage/ConfirmAccountRequiredPage'),
);
const RegisterPage = lazy(() => import('@/pages/RegisterPage/RegisterPage'));
const ErrorPage = lazy(() => import('@/pages/ErrorPage/ErrorPage'));
const LanguagePage = lazy(() => import('@/pages/LanguagePage/LanguagePage'));
const BrowserUnsupportedPage = lazy(
  () => import('@/pages/BrowserUnsupportedPage/BrowserUnsupportedPage'),
);
const ManagePage = lazy(() => import('@/pages/ManagePage/ManagePage'));
const EditProfilePage = lazy(() => import('@/pages/EditProfilePage/EditProfilePage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage/ChangePasswordPage'));
const SetPasswordPage = lazy(() => import('@/pages/SetPasswordPage/SetPasswordPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage/SupportPage'));
const SupportEditAccountPage = lazy(() => import('@/pages/SupportEditAccountPage'));
const SupportFederationInvitePage = lazy(() => import('@/pages/SupportFederationInvitePage'));

function renderLazyPage(Page: LazyExoticComponent<ComponentType>) {
  return (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  );
}

const LazyToaster = lazy(() => import('react-hot-toast').then((m) => ({ default: m.Toaster })));

function AuthOutlet() {
  return (
    <RequireAuth>
      <Suspense fallback={null}>
        <LazyToaster position="top-right" />
      </Suspense>
      <Outlet />
    </RequireAuth>
  );
}

function SupportOutlet() {
  return (
    <RequireAuth requiredRole="Support">
      <Outlet />
    </RequireAuth>
  );
}

const rawBase = import.meta.env.BASE_URL;
const basename = rawBase !== '/' ? rawBase.replace(/\/$/, '') : undefined;

export const router = createBrowserRouter(
  [
    { path: SPA_ROUTES.LOGIN, element: <LoginPage /> },
    { path: SPA_ROUTES.APP_LAUNCHING, element: renderLazyPage(AppLaunchingPage) },
    { path: SPA_ROUTES.FEDERATED_LOGIN, element: renderLazyPage(FederatedLoginPage) },
    { path: SPA_ROUTES.LOGOUT, element: renderLazyPage(LogoutPage) },
    { path: SPA_ROUTES.FORGOT_PASSWORD, element: renderLazyPage(ForgotPasswordPage) },
    {
      path: SPA_ROUTES.FORGOT_PASSWORD_CONFIRM,
      element: renderLazyPage(ForgotPasswordConfirmationPage),
    },
    { path: SPA_ROUTES.LOCKOUT, element: renderLazyPage(LockoutPage) },
    { path: SPA_ROUTES.MFA_CHALLENGE, element: renderLazyPage(MfaChallengePage) },
    { path: SPA_ROUTES.ACCESS_DENIED, element: renderLazyPage(AccessDeniedPage) },
    { path: SPA_ROUTES.RESET_PASSWORD, element: renderLazyPage(ResetPasswordPage) },
    {
      path: SPA_ROUTES.RESET_PASSWORD_CONFIRM,
      element: renderLazyPage(ResetPasswordConfirmationPage),
    },
    {
      path: SPA_ROUTES.PASSWORD_RESET_REQUIRED,
      element: renderLazyPage(PasswordResetRequiredPage),
    },
    { path: SPA_ROUTES.CONFIRM_ACCOUNT, element: renderLazyPage(ConfirmAccountPage) },
    {
      path: SPA_ROUTES.CONFIRM_ACCOUNT_REQUIRED,
      element: renderLazyPage(ConfirmAccountRequiredPage),
    },
    { path: SPA_ROUTES.REGISTER, element: renderLazyPage(RegisterPage) },
    { path: SPA_ROUTES.ERROR, element: renderLazyPage(ErrorPage) },
    { path: SPA_ROUTES.LANGUAGE, element: renderLazyPage(LanguagePage) },
    {
      path: SPA_ROUTES.BROWSER_UNSUPPORTED,
      element: renderLazyPage(BrowserUnsupportedPage),
    },
    {
      element: <AuthOutlet />,
      children: [
        { path: SPA_ROUTES.MANAGE, element: renderLazyPage(ManagePage) },
        { path: SPA_ROUTES.EDIT_PROFILE, element: renderLazyPage(EditProfilePage) },
        { path: SPA_ROUTES.CHANGE_PASSWORD, element: renderLazyPage(ChangePasswordPage) },
        { path: SPA_ROUTES.SET_PASSWORD, element: renderLazyPage(SetPasswordPage) },
        {
          element: <SupportOutlet />,
          children: [
            { path: SPA_ROUTES.SUPPORT, element: renderLazyPage(SupportPage) },
            {
              path: SPA_ROUTES.SUPPORT_EDIT_ACCOUNT,
              element: renderLazyPage(SupportEditAccountPage),
            },
            {
              path: SPA_ROUTES.SUPPORT_FEDERATION_INVITE,
              element: renderLazyPage(SupportFederationInvitePage),
            },
          ],
        },
      ],
    },
  ],
  { basename },
);
